import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { StreamChat } from 'stream-chat';
import { useAuth } from './AuthContext';
import { useStreamToken } from '../hooks/useMessagesData';
import { parseChannelShow } from '../utils/streamChannels';
import { emulatorConfig } from '../emulatorMode';

/**
 * App-level owner of the Stream Chat connection.
 *
 * The connect/disconnect logic lived inside MessagesPanelProvider, which is
 * mounted per show page - so the connection only existed once a show was
 * opened, the dashboard had no cross-show unread signal at all, and a second
 * chat surface (the unified Messages inbox) would have meant two independent
 * connectUser effects racing each other on the process-wide singleton. One
 * owner, mounted once in App.tsx, removes the race structurally and makes the
 * sidebar unread badge available from login.
 *
 * Only `stream-chat` (the client) is imported here - never `stream-chat-react`,
 * which stays confined to the lazy panel/inbox chunks. The client must be
 * eager anyway: the badges depend on the live socket.
 */

interface StreamClientContextValue {
  client: StreamChat | null;
  isConnecting: boolean;
  connectError: string | null;
  /** Cross-show unread total. Stream excludes muted channels from it. */
  totalUnread: number;
  /**
   * Unread messages in conversations the INBOX can actually show - direct
   * messages and the shows' group chats (its two tabs). This is the sidebar
   * badge: a count the user can always drive to zero by reading. Channels
   * with unrecognised ids stay excluded, unlike Stream's global total.
   */
  messagesUnread: number;
}

const StreamClientContext = createContext<StreamClientContextValue | null>(null);

/// connectUser must run exactly once per user even if two effects fire (React
/// StrictMode double-invokes effects in dev). Module-level so remounts of the
/// provider share the same in-flight promise.
let connectPromise: { userId: string; promise: Promise<void> } | null = null;

export const StreamClientProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { data: streamCredentials, error: tokenError } = useStreamToken(user?.uid);

  const [client, setClient] = useState<StreamChat | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [totalUnread, setTotalUnread] = useState(0);
  const [scopedUnread, setScopedUnread] = useState(0);

  useEffect(() => {
    let mounted = true;
    const initChat = async () => {
      if (!user || !streamCredentials) return;
      // Emulator mode (the E2E suite): the token came from the local stub,
      // and there is no local stand-in for Stream's websocket - connecting
      // would reach the real service. Chat shows its unavailable state.
      if (emulatorConfig()) {
        if (mounted) setConnectError('Chat is not available against the local emulators');
        return;
      }
      setIsConnecting(true);
      setConnectError(null);
      try {
        const { token, apiKey, userId } = streamCredentials;
        const streamClient = StreamChat.getInstance(apiKey);

        if (streamClient.user?.id === userId) {
          if (mounted) setClient(streamClient);
          return;
        }

        if (!connectPromise || connectPromise.userId !== userId) {
          connectPromise = {
            userId,
            promise: (async () => {
              if (streamClient.user && streamClient.user.id !== userId) {
                await streamClient.disconnectUser();
              }
              // Connect with id only - the server (onProfileWriteSyncStreamUser
              // trigger + generateStreamToken upsert) is the source of truth
              // for name/image. Passing them here would overwrite the server
              // data with whatever stale value the client has cached.
              await streamClient.connectUser({ id: userId }, token);
            })(),
          };
        }
        await connectPromise.promise;

        if (mounted) setClient(streamClient);
      } catch (err: any) {
        connectPromise = null;
        console.error('StreamClient: connect failed', err);
        if (mounted) setConnectError(err?.message || 'Failed to connect to chat');
      } finally {
        if (mounted) setIsConnecting(false);
      }
    };

    initChat();
    return () => {
      mounted = false;
    };
  }, [streamCredentials, user?.uid, user]);

  // On logout (user becomes null) disconnect the Stream client so the
  // WebSocket + push-device registration don't outlive the session. This is
  // deliberately logout-only: StreamChat.getInstance is a process-wide
  // singleton holding the live socket, so tearing it down on an ordinary
  // unmount would churn the connection (web equivalent of the mobile H17
  // teardown gap).
  useEffect(() => {
    if (user) return;
    if (!client) return;
    connectPromise = null;
    client.disconnectUser().catch((e) =>
      console.warn('StreamClient: disconnect on logout failed', e),
    );
    setClient(null);
    setTotalUnread(0);
    setScopedUnread(0);
  }, [user, client]);

  useEffect(() => {
    if (tokenError) setConnectError((tokenError as Error).message);
  }, [tokenError]);

  // Cross-show unread total. Seeded from the own-user object that connectUser
  // resolved with; kept live from the events Stream stamps a fresh
  // total_unread_count onto. No channel querying involved, so the badge is
  // correct even when no chat surface has ever been opened.
  useEffect(() => {
    if (!client) return;

    const seed = (client.user as any)?.total_unread_count;
    if (typeof seed === 'number') {
      setTotalUnread(seed);
    } else {
      // Older own-user payloads without the field: one explicit fetch.
      client
        .getUnreadCount()
        .then((res) => setTotalUnread(res.total_unread_count || 0))
        .catch(() => {});
    }

    const onEvent = (event: any) => {
      if (typeof event.total_unread_count === 'number') {
        setTotalUnread(event.total_unread_count);
      }
    };
    // `.on(handler)` without a type subscribes to every event - exactly what
    // is wanted, since several event types carry total_unread_count
    // (notification.message_new, message.new, notification.mark_read, ...).
    const subscription = client.on(onEvent);
    return () => subscription.unsubscribe();
  }, [client]);

  // Unread scoped to what the Messages inbox can actually list: DMs and
  // all-cast group chats (its Direct and Group tabs). Stream's global total
  // also counts unrecognised channel kinds, which once produced a sidebar
  // count that could not be cleared by reading everything on screen.
  // getUnreadCount() returns a per-channel breakdown, so only listable
  // channels count.
  useEffect(() => {
    if (!client) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const recompute = async () => {
      try {
        const res = await client.getUnreadCount();
        if (cancelled) return;
        let count = 0;
        for (const entry of res.channels || []) {
          // channel_id here is the full CID ("messaging:dm_..."), despite the
          // field name - verified against the live API.
          const id = entry.channel_id.includes(':')
            ? entry.channel_id.split(':')[1]
            : entry.channel_id;
          if (!parseChannelShow(id)) continue;
          count += entry.unread_count || 0;
        }
        setScopedUnread(count);
      } catch {
        // Keep the previous value; the next event retries.
      }
    };

    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(recompute, 1200); // debounce event bursts
    };

    recompute();
    const subscription = client.on((event: any) => {
      if (
        ['message.new', 'notification.message_new', 'notification.mark_read',
          'notification.mark_unread', 'notification.added_to_channel'].includes(event.type)
      ) {
        schedule();
      }
    });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, [client]);

  // Dev-only debug handle so a driven browser can assert indicator state.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      (window as any).__limelightChatDebug = { scopedUnread, totalUnread };
    }
  }, [scopedUnread, totalUnread]);

  const value = useMemo<StreamClientContextValue>(
    () => ({
      client,
      isConnecting,
      connectError,
      totalUnread,
      messagesUnread: scopedUnread,
    }),
    [client, isConnecting, connectError, totalUnread, scopedUnread],
  );

  return (
    <StreamClientContext.Provider value={value}>
      {children}
    </StreamClientContext.Provider>
  );
};

export const useStreamClient = (): Omit<StreamClientContextValue, 'totalUnread'> => {
  const ctx = useContext(StreamClientContext);
  if (!ctx) throw new Error('useStreamClient must be used within StreamClientProvider');
  return ctx;
};

/** The sidebar badge. Zero until the connection is up; live thereafter. */
export const useStreamUnread = (): number => {
  const ctx = useContext(StreamClientContext);
  if (!ctx) throw new Error('useStreamUnread must be used within StreamClientProvider');
  return ctx.totalUnread;
};

/** The sidebar Messages badge: unread the inbox can actually show. */
export const useMessagesUnread = (): number => {
  const ctx = useContext(StreamClientContext);
  if (!ctx) throw new Error('useMessagesUnread must be used within StreamClientProvider');
  return ctx.messagesUnread;
};
